import React, { useEffect, useMemo, useState } from 'react';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import { formatMatchDate, formatMatchTime, normalizeMatchdaySettings, selectMatchdayMatches } from '../../lib/matchday';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiZap, FiClock, FiLayers, FiStar, FiMousePointer, FiCheckCircle, FiAlertCircle } = FiIcons;

const defaults = {
  matchday_mode_enabled: true,
  matchday_window_days: 5,
  matchday_threshold_hours: 24,
  matchday_live_fallback_hours: 3,
  matchday_upcoming_count: 2,
  matchday_selection_mode: 'automatic',
  matchday_manual_match_id: '',
};

const MatchLabel = ({ match }) => (
  <>
    {formatMatchDate(match)} {formatMatchTime(match)} — {match.home_team} vs {match.away_team}
  </>
);

const MatchdaySettings = () => {
  const [configId, setConfigId] = useState(null);
  const [form, setForm] = useState(defaults);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const load = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    const today = new Date().toISOString().slice(0, 10);
    const [configResult, matchesResult] = await Promise.all([
      supabase
        .from('site_config_aox1234')
        .select('id,matchday_mode_enabled,matchday_window_days,matchday_threshold_hours,matchday_live_fallback_hours,matchday_upcoming_count,matchday_selection_mode,matchday_manual_match_id')
        .limit(1)
        .single(),
      supabase
        .from('matches_aox1234')
        .select('id,date,time,home_team,away_team,status,is_featured,team_category,tournament')
        .gte('date', today)
        .order('date', { ascending: true })
        .order('time', { ascending: true }),
    ]);

    if (configResult.error) {
      setMessage({ type: 'error', text: configResult.error.message });
    } else {
      setConfigId(configResult.data.id);
      setForm({
        ...defaults,
        ...configResult.data,
        matchday_manual_match_id: configResult.data.matchday_manual_match_id || '',
      });
    }

    if (matchesResult.error) {
      setMessage({ type: 'error', text: matchesResult.error.message });
    } else {
      setMatches((matchesResult.data || []).filter((match) => !['completed', 'canceled', 'postponed'].includes(match.status)));
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const settingsForPreview = useMemo(() => normalizeMatchdaySettings({
    ...form,
    matchday_window_days: Number(form.matchday_window_days),
    matchday_threshold_hours: Number(form.matchday_threshold_hours),
    matchday_live_fallback_hours: Number(form.matchday_live_fallback_hours),
    matchday_upcoming_count: Number(form.matchday_upcoming_count),
    matchday_manual_match_id: form.matchday_manual_match_id || null,
  }), [form]);

  const preview = useMemo(
    () => selectMatchdayMatches(matches, new Date(), settingsForPreview),
    [matches, settingsForPreview]
  );

  const save = async (event) => {
    event.preventDefault();
    if (!configId) return;

    setSaving(true);
    setMessage({ type: '', text: '' });

    const payload = {
      matchday_mode_enabled: Boolean(form.matchday_mode_enabled),
      matchday_window_days: Number(form.matchday_window_days),
      matchday_threshold_hours: Number(form.matchday_threshold_hours),
      matchday_live_fallback_hours: Number(form.matchday_live_fallback_hours),
      matchday_upcoming_count: Number(form.matchday_upcoming_count),
      matchday_selection_mode: form.matchday_selection_mode,
      matchday_manual_match_id: form.matchday_selection_mode === 'manual' && form.matchday_manual_match_id
        ? form.matchday_manual_match_id
        : null,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase.from('site_config_aox1234').update(payload).eq('id', configId);

    setSaving(false);
    if (error) {
      setMessage({ type: 'error', text: `Σφάλμα: ${error.message}` });
    } else {
      setMessage({ type: 'success', text: 'Οι ρυθμίσεις Matchday αποθηκεύτηκαν.' });
      setForm((prev) => ({ ...prev, matchday_manual_match_id: payload.matchday_manual_match_id || '' }));
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-500">Φόρτωση ρυθμίσεων Matchday...</div>;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-7">
          <div className="inline-flex items-center gap-2 text-primary-700 font-bold text-sm uppercase tracking-wider"><SafeIcon icon={FiZap} className="w-4 h-4" /> Matchday Mode</div>
          <h1 className="text-3xl font-black text-gray-900 mt-2">Ρυθμίσεις Matchday</h1>
          <p className="text-gray-600 mt-2">Ελέγξτε πότε ενεργοποιείται το Matchday hero και ποιος αγώνας παίρνει προτεραιότητα.</p>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} className="w-5 h-5" />
            {message.text}
          </div>
        )}

        <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-[1.2fr_.8fr] gap-6">
          <div className="space-y-6">
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <label className="flex items-start gap-4 cursor-pointer">
                <input type="checkbox" name="matchday_mode_enabled" checked={Boolean(form.matchday_mode_enabled)} onChange={handleChange} className="mt-1 h-5 w-5 rounded border-gray-300 text-primary-600" />
                <div>
                  <div className="font-bold text-gray-900">Ενεργό Matchday Mode</div>
                  <div className="text-sm text-gray-500 mt-1">Αν είναι κλειστό, η αρχική κρατά πάντα το κανονικό hero carousel.</div>
                </div>
              </label>
            </section>

            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="font-bold text-lg text-gray-900 flex items-center"><SafeIcon icon={FiClock} className="w-5 h-5 mr-2 text-primary-600" />Χρονικά όρια</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Εμφάνιση Χ ημέρες πριν</span>
                  <input type="number" min="0" max="30" name="matchday_window_days" value={form.matchday_window_days} onChange={handleChange} className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2" />
                  <span className="text-xs text-gray-500">0 = μόνο Matchday/Live.</span>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">MATCHDAY πριν (ώρες)</span>
                  <input type="number" min="1" max="72" name="matchday_threshold_hours" value={form.matchday_threshold_hours} onChange={handleChange} className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2" />
                  <span className="text-xs text-gray-500">Π.χ. 24 = μία ημέρα πριν.</span>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Live fallback (ώρες)</span>
                  <input type="number" min="1" max="12" name="matchday_live_fallback_hours" value={form.matchday_live_fallback_hours} onChange={handleChange} className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2" />
                  <span className="text-xs text-gray-500">Αν ξεχαστεί το status LIVE.</span>
                </label>
              </div>
            </section>

            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="font-bold text-lg text-gray-900 flex items-center"><SafeIcon icon={FiStar} className="w-5 h-5 mr-2 text-primary-600" />Προτεραιότητα αγώνα</h2>
              <div className="mt-5 grid gap-3">
                {[
                  ['automatic', 'Αυτόματο — κοντινότερος αγώνας', 'LIVE έχει πάντα προτεραιότητα και μετά εμφανίζεται ο χρονικά κοντινότερος αγώνας.'],
                  ['featured', 'Featured πρώτα', 'LIVE πρώτα, μετά ο πρώτος αγώνας με Featured ενεργό και μετά ο κοντινότερος.'],
                  ['manual', 'Χειροκίνητη επιλογή', 'LIVE πρώτα και έπειτα ο αγώνας που επιλέγετε εδώ.'],
                ].map(([value, title, help]) => (
                  <label key={value} className={`block rounded-xl border p-4 cursor-pointer ${form.matchday_selection_mode === value ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
                    <div className="flex gap-3">
                      <input type="radio" name="matchday_selection_mode" value={value} checked={form.matchday_selection_mode === value} onChange={handleChange} className="mt-1" />
                      <div><div className="font-semibold text-gray-900">{title}</div><div className="text-sm text-gray-500 mt-1">{help}</div></div>
                    </div>
                  </label>
                ))}
              </div>

              {form.matchday_selection_mode === 'manual' && (
                <label className="block mt-5">
                  <span className="text-sm font-medium text-gray-700">Κύριος αγώνας</span>
                  <select name="matchday_manual_match_id" value={form.matchday_manual_match_id} onChange={handleChange} className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2">
                    <option value="">— Επιλέξτε αγώνα —</option>
                    {matches.map((match) => <option key={match.id} value={match.id}>{formatMatchDate(match)} {formatMatchTime(match)} — {match.home_team} vs {match.away_team}</option>)}
                  </select>
                </label>
              )}
            </section>

            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="font-bold text-lg text-gray-900 flex items-center"><SafeIcon icon={FiLayers} className="w-5 h-5 mr-2 text-primary-600" />Πόσοι αγώνες στο hero</h2>
              <div className="mt-5 flex gap-3">
                {[1, 2, 3].map((count) => (
                  <label key={count} className={`flex-1 rounded-xl border p-4 text-center cursor-pointer ${Number(form.matchday_upcoming_count) === count ? 'border-primary-500 bg-primary-50 text-primary-800' : 'border-gray-200 text-gray-700'}`}>
                    <input className="sr-only" type="radio" name="matchday_upcoming_count" value={count} checked={Number(form.matchday_upcoming_count) === count} onChange={handleChange} />
                    <div className="text-2xl font-black">{count}</div>
                    <div className="text-xs mt-1">{count === 1 ? 'μόνο κύριος' : `${count - 1} επόμεν${count === 2 ? 'ος' : 'οι'} + κύριος`}</div>
                  </label>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-4">Όταν υπάρχουν δύο ή τρεις αγώνες μέσα στο παράθυρο, ο κύριος μένει cinematic και οι υπόλοιποι εμφανίζονται ως compact «Ακολουθεί» cards.</p>
            </section>

            <button type="submit" disabled={saving} className="w-full md:w-auto inline-flex items-center justify-center rounded-xl bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-7 py-3 font-bold shadow-lg">
              <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />{saving ? 'Αποθήκευση...' : 'Αποθήκευση Matchday'}
            </button>
          </div>

          <aside className="space-y-6">
            <section className="bg-gradient-to-br from-primary-950 via-primary-800 to-blue-600 text-white rounded-2xl shadow-lg p-6 lg:sticky lg:top-6">
              <div className="flex items-center gap-2 text-blue-200 text-xs font-black uppercase tracking-[0.16em]"><SafeIcon icon={FiMousePointer} className="w-4 h-4" /> Live Preview Logic</div>
              <h2 className="text-xl font-black mt-3">Τι θα εμφανιστεί τώρα</h2>

              {!form.matchday_mode_enabled ? (
                <div className="mt-5 rounded-xl bg-black/20 border border-white/10 p-4 text-sm">Matchday Mode είναι απενεργοποιημένο. Θα εμφανιστεί το κανονικό hero carousel.</div>
              ) : preview.primary ? (
                <div className="mt-5 space-y-3">
                  <div className="rounded-xl bg-black/20 border border-white/10 p-4">
                    <div className="text-xs text-blue-200 font-bold uppercase tracking-wider">Κύριος</div>
                    <div className="font-black mt-2"><MatchLabel match={preview.primary} /></div>
                    {preview.primary.is_featured && <div className="mt-2 text-xs text-yellow-300">★ Featured</div>}
                  </div>
                  {preview.following.map((match) => (
                    <div key={match.id} className="rounded-xl bg-white/10 border border-white/10 p-4">
                      <div className="text-xs text-blue-200 font-bold uppercase tracking-wider">Ακολουθεί</div>
                      <div className="font-semibold mt-2 text-sm"><MatchLabel match={match} /></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-5 rounded-xl bg-black/20 border border-white/10 p-4 text-sm">Δεν υπάρχει αγώνας μέσα στο τρέχον παράθυρο των {form.matchday_window_days} ημερών.</div>
              )}

              <div className="mt-6 pt-5 border-t border-white/10 text-xs text-blue-100 space-y-2">
                <p>• LIVE έχει πάντα απόλυτη προτεραιότητα.</p>
                <p>• Οι αναβληθέντες/ακυρωμένοι δεν μπαίνουν στο Matchday hero.</p>
                <p>• Featured/Manual επηρεάζουν μόνο αγώνες μέσα στο ενεργό χρονικό παράθυρο.</p>
              </div>
            </section>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default MatchdaySettings;
